import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Truck, Package, Clock, CheckCircle2, RefreshCw, IndianRupee, Factory, Send, PackageCheck, FileText, ClipboardList, ArrowRightLeft, History } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import { formatCurrency, formatQuantity } from '@/utils/formatters';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  issued: 'bg-yellow-100 text-yellow-800',
  material_sent: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-purple-100 text-purple-800',
  partial_received: 'bg-orange-100 text-orange-800',
  partial: 'bg-orange-100 text-orange-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-800'
};

const statusLabels = {
  issued: 'Issued',
  material_sent: 'Material Sent',
  in_progress: 'In Progress',
  partial_received: 'Partial Received',
  partial: 'Partial',
  completed: 'Completed',
  cancelled: 'Cancelled'
};

export default function OJCRegisterPage() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [ojcList, setOjcList] = useState([]);
  const [summary, setSummary] = useState({});
  const [activeTab, setActiveTab] = useState('pending');
  
  // Data for new features
  const [suppliers, setSuppliers] = useState([]);
  const [pendingDispatchOjcs, setPendingDispatchOjcs] = useState([]);
  const [dispatchRegister, setDispatchRegister] = useState([]);
  const [receiptRegister, setReceiptRegister] = useState([]);
  
  // Receive dialog state
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [eligibleSrLines, setEligibleSrLines] = useState([]);
  const [selectedSrLine, setSelectedSrLine] = useState('');
  const [selectedOjc, setSelectedOjc] = useState(null);
  const [receiveForm, setReceiveForm] = useState({
    quantity_received: '',
    quantity_accepted: '',
    quantity_rejected: '0',
    rejection_reason: '',
    labour_charges: '',
    other_charges: '0',
    dc_no: '',
    remarks: ''
  });
  
  // Detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailOjc, setDetailOjc] = useState(null);
  
  // Dispatch dialog state
  const [dispatchDialogOpen, setDispatchDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [vendorOjcs, setVendorOjcs] = useState([]);
  const [selectedOjcsForDispatch, setSelectedOjcsForDispatch] = useState({});
  const [dispatchForm, setDispatchForm] = useState({
    dispatch_date: new Date().toISOString().split('T')[0],
    vehicle_number: '',
    driver_name: '',
    driver_phone: '',
    expected_return_date: '',
    remarks: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ojcRes, summaryRes, suppliersRes] = await Promise.all([
        axios.get(`${API_URL}/ojc/register`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/ojc/summary`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/suppliers`, { headers: getAuthHeader() })
      ]);
      
      setOjcList(ojcRes.data);
      setSummary(summaryRes.data);
      // Show all suppliers - any supplier can be a jobber
      setSuppliers(suppliersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load OJC data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDispatchData = async () => {
    try {
      const [pendingRes, dispatchesRes, receiptsRes] = await Promise.all([
        axios.get(`${API_URL}/ojc/pending-dispatch`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/ojc-dispatches`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/ojc-receipts`, { headers: getAuthHeader() })
      ]);
      
      setPendingDispatchOjcs(pendingRes.data);
      setDispatchRegister(dispatchesRes.data);
      setReceiptRegister(receiptsRes.data);
    } catch (error) {
      console.error('Error fetching dispatch data:', error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchDispatchData();
  }, []);

  const openReceiveDialog = async (ojc) => {
    setSelectedOjc(ojc);
    setReceiveForm({
      quantity_received: '',
      quantity_accepted: '',
      quantity_rejected: '0',
      rejection_reason: '',
      labour_charges: '',
      other_charges: '0',
      dc_no: '',
      remarks: ''
    });
    // OJC inward redesign Phase 1 (Apr 2026) — for non-grandfathered OJCs,
    // fetch eligible Store Receipt lines (routed_to=ojc, matching ojc_register_id,
    // not yet consumed). Grandfathered OJCs bypass this step and use the old
    // direct-receive flow for backward compatibility.
    setEligibleSrLines([]);
    setSelectedSrLine('');
    if (!ojc.grandfathered) {
      try {
        const resp = await axios.get(
          `${API_URL}/store/receipts?routed_to=ojc`,
          { headers: getAuthHeader() }
        );
        const allSrs = Array.isArray(resp.data)
          ? resp.data
          : (resp.data?.receipts || resp.data?.data || []);
        const eligible = [];
        for (const sr of allSrs) {
          for (const line of (sr.items || [])) {
            if (line.ojc_register_id === ojc.id && line.consumed !== true) {
              eligible.push({
                sr_id: sr.id,
                sr_no: sr.store_receipt_no,
                line_id: line.line_id,
                quantity_received: line.quantity_received || 0,
                item_name: line.item_name,
                created_at: sr.created_at,
              });
            }
          }
        }
        setEligibleSrLines(eligible);
        if (!eligible.length) {
          toast.info(
            'No open Store Receipts linked to this OJC. Create a Jobwork-Own Gate Entry + Store Receipt (with this OJC linked) first, then come back to Receive.'
          );
        }
      } catch (err) {
        console.error('Failed to fetch eligible Store Receipts:', err);
      }
    }
    setReceiveDialogOpen(true);
  };

  const openDetailDialog = async (ojc) => {
    try {
      const response = await axios.get(`${API_URL}/ojc/${ojc.id}`, { headers: getAuthHeader() });
      setDetailOjc(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load OJC details');
    }
  };

  const handleMarkSent = async (ojc) => {
    try {
      await axios.patch(`${API_URL}/ojc/${ojc.id}/mark-sent`, {}, { headers: getAuthHeader() });
      toast.success('OJC marked as material sent');
      fetchData();
      fetchDispatchData();
    } catch (error) {
      toast.error('Failed to update OJC');
    }
  };

  const handleReceive = async () => {
    const qtyReceived = parseFloat(receiveForm.quantity_received) || 0;
    const qtyAccepted = parseFloat(receiveForm.quantity_accepted) || 0;
    const qtyRejected = parseFloat(receiveForm.quantity_rejected) || 0;
    const labourCharges = parseFloat(receiveForm.labour_charges) || 0;
    
    if (qtyReceived <= 0) {
      toast.error('Please enter quantity received');
      return;
    }
    
    if (qtyAccepted + qtyRejected !== qtyReceived) {
      toast.error('Accepted + Rejected must equal Received quantity');
      return;
    }
    
    if (labourCharges <= 0) {
      toast.error('Please enter labour charges');
      return;
    }
    
    // OJC inward redesign Phase 1 (Apr 2026) — non-grandfathered OJC requires SR selection
    if (selectedOjc && !selectedOjc.grandfathered && !selectedSrLine) {
      toast.error('Please select a linked Store Receipt for this OJC');
      return;
    }
    if (qtyRejected > 0 && !(receiveForm.rejection_reason || '').trim()) {
      toast.error('Rejection reason is required when any qty is rejected');
      return;
    }
    
    try {
      const payload = {
        ojc_id: selectedOjc.id,
        quantity_received: qtyReceived,
        quantity_accepted: qtyAccepted,
        quantity_rejected: qtyRejected,
        rejection_reason: receiveForm.rejection_reason || null,
        labour_charges: labourCharges,
        other_charges: parseFloat(receiveForm.other_charges) || 0,
        dc_no: receiveForm.dc_no || null,
        remarks: receiveForm.remarks || null
      };
      if (selectedSrLine) {
        const [srId, lineId] = selectedSrLine.split('|');
        payload.store_receipt_id = srId;
        payload.line_id = lineId;
      }
      
      const response = await axios.post(`${API_URL}/ojc/receive`, payload, { headers: getAuthHeader() });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setReceiveDialogOpen(false);
        fetchData();
        fetchDispatchData();
      }
    } catch (error) {
      console.error('Error receiving OJC:', error);
      toast.error(error.response?.data?.detail || 'Failed to receive OJC batch');
    }
  };

  // Dispatch Challan Functions
  const openDispatchDialog = () => {
    setSelectedVendor('');
    setVendorOjcs([]);
    setSelectedOjcsForDispatch({});
    setDispatchForm({
      dispatch_date: new Date().toISOString().split('T')[0],
      vehicle_number: '',
      driver_name: '',
      driver_phone: '',
      expected_return_date: '',
      remarks: ''
    });
    setDispatchDialogOpen(true);
  };

  const handleVendorSelect = async (vendorId) => {
    setSelectedVendor(vendorId);
    setSelectedOjcsForDispatch({});
    
    if (vendorId) {
      try {
        const response = await axios.get(`${API_URL}/ojc/pending-dispatch?jobber_id=${vendorId}`, { headers: getAuthHeader() });
        setVendorOjcs(response.data);
      } catch (error) {
        console.error('Error fetching vendor OJCs:', error);
        setVendorOjcs([]);
      }
    } else {
      setVendorOjcs([]);
    }
  };

  const toggleOjcSelection = (ojcId, pendingQty) => {
    setSelectedOjcsForDispatch(prev => {
      if (prev[ojcId]) {
        const { [ojcId]: removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [ojcId]: pendingQty };
    });
  };

  const updateDispatchQty = (ojcId, qty) => {
    setSelectedOjcsForDispatch(prev => ({
      ...prev,
      [ojcId]: parseFloat(qty) || 0
    }));
  };

  const handleCreateDispatch = async () => {
    const selectedItems = Object.entries(selectedOjcsForDispatch)
      .filter(([_, qty]) => qty > 0)
      .map(([ojcId, qty]) => ({
        ojc_id: ojcId,
        dispatch_qty: qty,
        is_redispatch: false
      }));
    
    if (selectedItems.length === 0) {
      toast.error('Please select at least one OJC to dispatch');
      return;
    }
    
    if (!selectedVendor) {
      toast.error('Please select a vendor');
      return;
    }
    
    try {
      const payload = {
        jobber_id: selectedVendor,
        dispatch_items: selectedItems,
        dispatch_date: dispatchForm.dispatch_date,
        vehicle_number: dispatchForm.vehicle_number,
        driver_name: dispatchForm.driver_name,
        driver_phone: dispatchForm.driver_phone,
        expected_return_date: dispatchForm.expected_return_date || null,
        remarks: dispatchForm.remarks
      };
      
      const response = await axios.post(`${API_URL}/ojc-dispatches`, payload, { headers: getAuthHeader() });
      
      toast.success(`Dispatch Challan ${response.data.dc_number} created successfully!`);
      setDispatchDialogOpen(false);
      fetchData();
      fetchDispatchData();
    } catch (error) {
      console.error('Error creating dispatch:', error);
      toast.error(error.response?.data?.detail || 'Failed to create dispatch');
    }
  };

  // Auto-calculate accepted when received changes
  const handleReceivedChange = (value) => {
    const qty = parseFloat(value) || 0;
    const rejected = parseFloat(receiveForm.quantity_rejected) || 0;
    setReceiveForm({
      ...receiveForm,
      quantity_received: value,
      quantity_accepted: (qty - rejected).toString()
    });
  };

  const handleRejectedChange = (value) => {
    const rejected = parseFloat(value) || 0;
    const received = parseFloat(receiveForm.quantity_received) || 0;
    setReceiveForm({
      ...receiveForm,
      quantity_rejected: value,
      quantity_accepted: (received - rejected).toString()
    });
  };

  const pendingOjcs = ojcList.filter(o => ['issued', 'material_sent', 'in_progress', 'partial_received', 'partial'].includes(o.status));
  const completedOjcs = ojcList.filter(o => o.status === 'completed');
  
  // Get unique vendors with pending OJCs
  const vendorsWithPending = [...new Set(pendingDispatchOjcs.map(o => o.jobber_id))];

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Factory className="w-7 h-7 text-purple-600" />
              OJC Register
            </h1>
            <p className="text-muted-foreground">Outside Job Cards - Track outsourced process work</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={openDispatchDialog} variant="default" className="bg-blue-600 hover:bg-blue-700">
              <Truck className="w-4 h-4 mr-2" />
              Create Dispatch (DC)
            </Button>
            <Button onClick={() => { fetchData(); fetchDispatchData(); }} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Issued</p>
                  <p className="text-2xl font-bold">{summary.issued || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Send className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Material Sent</p>
                  <p className="text-2xl font-bold">{summary.material_sent || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Package className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Partial Received</p>
                  <p className="text-2xl font-bold">{summary.partial_received || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Completed</p>
                  <p className="text-2xl font-bold">{summary.completed || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <IndianRupee className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Cost</p>
                  <p className="text-xl font-bold">{formatCurrency(summary.total_ojc_cost || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="pending" data-testid="tab-pending">
              Pending ({pendingOjcs.length})
            </TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed">
              Completed ({completedOjcs.length})
            </TabsTrigger>
            <TabsTrigger value="dc-register" data-testid="tab-dc-register">
              <FileText className="w-4 h-4 mr-1" />
              DC Register ({dispatchRegister.length})
            </TabsTrigger>
            <TabsTrigger value="grn-register" data-testid="tab-grn-register">
              <ClipboardList className="w-4 h-4 mr-1" />
              GRN Register ({receiptRegister.length})
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">
              All ({ojcList.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Pending OJCs</CardTitle>
                <CardDescription>Outsourced jobs awaiting completion</CardDescription>
              </CardHeader>
              <CardContent>
                {pendingOjcs.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Factory className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No pending OJCs</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>OJC No.</TableHead>
                        <TableHead>Job No.</TableHead>
                        <TableHead>Process</TableHead>
                        <TableHead>Jobber</TableHead>
                        <TableHead className="text-center">Issued</TableHead>
                        <TableHead className="text-center">Dispatched</TableHead>
                        <TableHead className="text-center">Received</TableHead>
                        <TableHead className="text-center">Pending</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingOjcs.map(ojc => (
                        <TableRow key={ojc.id} data-testid={`ojc-row-${ojc.id}`}>
                          <TableCell className="font-mono font-bold text-purple-600">{ojc.ojc_number}</TableCell>
                          <TableCell className="font-mono text-sm">{ojc.job_number}</TableCell>
                          <TableCell>{ojc.process_name}</TableCell>
                          <TableCell>{ojc.jobber_name || '-'}</TableCell>
                          <TableCell className="text-center">{ojc.quantity_issued}</TableCell>
                          <TableCell className="text-center text-blue-600">{ojc.quantity_dispatched || 0}</TableCell>
                          <TableCell className="text-center text-green-600">{ojc.quantity_received || 0}</TableCell>
                          <TableCell className="text-center font-medium text-orange-600">{ojc.quantity_pending || 0}</TableCell>
                          <TableCell>
                            <Badge className={statusColors[ojc.status]}>
                              {statusLabels[ojc.status]}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              {ojc.status === 'issued' && (
                                <Button size="sm" variant="outline" onClick={() => handleMarkSent(ojc)} data-testid={`mark-sent-${ojc.id}`}>
                                  <Send className="w-4 h-4 mr-1" /> Mark Sent
                                </Button>
                              )}
                              {['material_sent', 'in_progress', 'partial_received', 'partial'].includes(ojc.status) && (ojc.quantity_pending || 0) > 0 && (
                                <Button size="sm" onClick={() => openReceiveDialog(ojc)} data-testid={`receive-${ojc.id}`}>
                                  <PackageCheck className="w-4 h-4 mr-1" /> Receive
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" onClick={() => openDetailDialog(ojc)} data-testid={`view-${ojc.id}`}>
                                View
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="completed" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Completed OJCs</CardTitle>
                <CardDescription>Fully received outsourced jobs with costing</CardDescription>
              </CardHeader>
              <CardContent>
                {completedOjcs.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No completed OJCs yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>OJC No.</TableHead>
                        <TableHead>Process</TableHead>
                        <TableHead>Jobber</TableHead>
                        <TableHead className="text-center">Qty Accepted</TableHead>
                        <TableHead className="text-center">Qty Rejected</TableHead>
                        <TableHead className="text-right">Labour Cost</TableHead>
                        <TableHead className="text-right">Total Cost</TableHead>
                        <TableHead className="text-right">Rate/Unit</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {completedOjcs.map(ojc => (
                        <TableRow key={ojc.id}>
                          <TableCell className="font-mono font-bold">{ojc.ojc_number}</TableCell>
                          <TableCell>{ojc.process_name}</TableCell>
                          <TableCell>{ojc.jobber_name || '-'}</TableCell>
                          <TableCell className="text-center text-green-600">{ojc.quantity_accepted}</TableCell>
                          <TableCell className="text-center text-red-600">{ojc.quantity_rejected || 0}</TableCell>
                          <TableCell className="text-right">{formatCurrency(ojc.total_labour_charges || 0)}</TableCell>
                          <TableCell className="text-right font-bold">{formatCurrency(ojc.total_ojc_cost || 0)}</TableCell>
                          <TableCell className="text-right">₹{(ojc.actual_rate_per_unit || 0).toFixed(2)}</TableCell>
                          <TableCell>
                            <Button size="sm" variant="ghost" onClick={() => openDetailDialog(ojc)}>
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* DC Register Tab */}
          <TabsContent value="dc-register" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-blue-600" />
                  Dispatch Challan Register
                </CardTitle>
                <CardDescription>All outward dispatch challans to vendors</CardDescription>
              </CardHeader>
              <CardContent>
                {dispatchRegister.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Truck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No dispatch challans yet</p>
                    <Button onClick={openDispatchDialog} className="mt-4">
                      Create First Dispatch
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>DC No.</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Process</TableHead>
                        <TableHead className="text-center">Items</TableHead>
                        <TableHead className="text-center">Total Qty</TableHead>
                        <TableHead>Vehicle</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dispatchRegister.map(dc => (
                        <TableRow key={dc.id} data-testid={`dc-row-${dc.id}`}>
                          <TableCell className="font-mono font-bold text-blue-600">{dc.dc_number}</TableCell>
                          <TableCell>{formatDate(dc.dispatch_date)}</TableCell>
                          <TableCell>{dc.jobber_name}</TableCell>
                          <TableCell>{dc.process_name || '-'}</TableCell>
                          <TableCell className="text-center">{dc.dispatch_items?.length || 0}</TableCell>
                          <TableCell className="text-center font-medium">{dc.total_qty}</TableCell>
                          <TableCell>{dc.vehicle_number || '-'}</TableCell>
                          <TableCell>
                            <Badge className={dc.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}>
                              {dc.status || 'dispatched'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* GRN Register Tab */}
          <TabsContent value="grn-register" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-green-600" />
                  GRN Register (OJC Receipts)
                </CardTitle>
                <CardDescription>All inward receipts from vendors</CardDescription>
              </CardHeader>
              <CardContent>
                {receiptRegister.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No GRNs yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>GRN No.</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Vendor DC No.</TableHead>
                        <TableHead className="text-center">Items</TableHead>
                        <TableHead className="text-center">OK Qty</TableHead>
                        <TableHead className="text-center">Rejected</TableHead>
                        <TableHead>Received By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {receiptRegister.map(grn => (
                        <TableRow key={grn.id} data-testid={`grn-row-${grn.id}`}>
                          <TableCell className="font-mono font-bold text-green-600">{grn.grn_number}</TableCell>
                          <TableCell>{formatDate(grn.receipt_date)}</TableCell>
                          <TableCell>{grn.jobber_name}</TableCell>
                          <TableCell>{grn.vendor_challan_no || '-'}</TableCell>
                          <TableCell className="text-center">{grn.receipt_items?.length || 0}</TableCell>
                          <TableCell className="text-center text-green-600 font-medium">{grn.total_received_ok}</TableCell>
                          <TableCell className="text-center text-red-600">{grn.total_rejected || 0}</TableCell>
                          <TableCell>{grn.received_by_name || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="all" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>All OJCs</CardTitle>
                <CardDescription>Complete OJC register</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>OJC No.</TableHead>
                      <TableHead>Job No.</TableHead>
                      <TableHead>Process</TableHead>
                      <TableHead>Work Order</TableHead>
                      <TableHead>Jobber</TableHead>
                      <TableHead className="text-center">Qty</TableHead>
                      <TableHead className="text-right">Total Cost</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ojcList.map(ojc => (
                      <TableRow key={ojc.id}>
                        <TableCell className="font-mono font-bold">{ojc.ojc_number}</TableCell>
                        <TableCell className="font-mono text-sm">{ojc.job_number}</TableCell>
                        <TableCell>{ojc.process_name}</TableCell>
                        <TableCell className="font-mono text-sm">{ojc.work_order_number}</TableCell>
                        <TableCell>{ojc.jobber_name || '-'}</TableCell>
                        <TableCell className="text-center">{ojc.quantity_issued}</TableCell>
                        <TableCell className="text-right">{formatCurrency(ojc.total_ojc_cost || 0)}</TableCell>
                        <TableCell>
                          <Badge className={statusColors[ojc.status]}>
                            {statusLabels[ojc.status]}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(ojc.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Receive Dialog */}
        <Dialog open={receiveDialogOpen} onOpenChange={setReceiveDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PackageCheck className="w-5 h-5" />
                Receive OJC Batch
              </DialogTitle>
              <DialogDescription>
                OJC: <span className="font-mono font-bold">{selectedOjc?.ojc_number}</span> | 
                Pending: <span className="font-bold">{selectedOjc?.quantity_pending}</span>
              </DialogDescription>
            </DialogHeader>
            
            {selectedOjc && (
              <div className="space-y-4 py-4">
                {/* OJC Info */}
                <div className="bg-muted/50 rounded-lg p-3 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <p><span className="text-muted-foreground">Process:</span> {selectedOjc.process_name}</p>
                    <p><span className="text-muted-foreground">Jobber:</span> {selectedOjc.jobber_name}</p>
                    <p><span className="text-muted-foreground">Issued Qty:</span> {selectedOjc.quantity_issued}</p>
                    <p><span className="text-muted-foreground">Already Received:</span> {selectedOjc.quantity_received}</p>
                  </div>
                </div>

                {/* OJC inward redesign Phase 1 (Apr 2026) — Store Receipt picker
                    for non-grandfathered OJCs only. */}
                {!selectedOjc.grandfathered && (
                  <div className="space-y-2 border border-amber-200 bg-amber-50/50 rounded-lg p-3">
                    <Label className="text-sm font-medium">
                      Linked Store Receipt * <span className="text-xs text-muted-foreground">(required)</span>
                    </Label>
                    {eligibleSrLines.length === 0 ? (
                      <p className="text-xs text-amber-800" data-testid="ojc-no-sr-msg">
                        No open Store Receipt lines found for this OJC. Create a Gate Entry
                        (Jobwork-Own) + Store Receipt (linked to this OJC) first, then return here.
                      </p>
                    ) : (
                      <select
                        className="w-full border rounded px-2 py-1.5 text-sm bg-white"
                        value={selectedSrLine}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSelectedSrLine(val);
                          const picked = eligibleSrLines.find(
                            (l) => `${l.sr_id}|${l.line_id}` === val
                          );
                          if (picked) {
                            setReceiveForm((f) => ({
                              ...f,
                              quantity_received: String(picked.quantity_received),
                              quantity_accepted: String(picked.quantity_received),
                              quantity_rejected: '0',
                            }));
                          }
                        }}
                        data-testid="ojc-sr-line-picker"
                      >
                        <option value="">— Select a Store Receipt line —</option>
                        {eligibleSrLines.map((l) => (
                          <option key={`${l.sr_id}|${l.line_id}`} value={`${l.sr_id}|${l.line_id}`}>
                            {l.sr_no} · {l.item_name} · {l.quantity_received}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {/* Quantity Fields */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Qty Received *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0"
                      value={receiveForm.quantity_received}
                      onChange={(e) => handleReceivedChange(e.target.value)}
                      data-testid="receive-qty-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Qty Accepted *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0"
                      value={receiveForm.quantity_accepted}
                      onChange={(e) => setReceiveForm({ ...receiveForm, quantity_accepted: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Qty Rejected</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0"
                      value={receiveForm.quantity_rejected}
                      onChange={(e) => handleRejectedChange(e.target.value)}
                    />
                  </div>
                </div>

                {parseFloat(receiveForm.quantity_rejected) > 0 && (
                  <div className="space-y-2">
                    <Label>Rejection Reason</Label>
                    <Input
                      placeholder="Reason for rejection"
                      value={receiveForm.rejection_reason}
                      onChange={(e) => setReceiveForm({ ...receiveForm, rejection_reason: e.target.value })}
                    />
                  </div>
                )}

                {/* Cost Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Labour Charges *</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                      <Input
                        type="number"
                        step="0.01"
                        className="pl-7"
                        placeholder="0.00"
                        value={receiveForm.labour_charges}
                        onChange={(e) => setReceiveForm({ ...receiveForm, labour_charges: e.target.value })}
                        data-testid="labour-charges-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Other Charges</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                      <Input
                        type="number"
                        step="0.01"
                        className="pl-7"
                        placeholder="0.00"
                        value={receiveForm.other_charges}
                        onChange={(e) => setReceiveForm({ ...receiveForm, other_charges: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* DC and Remarks */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>DC No. (from Jobber)</Label>
                    <Input
                      placeholder="Delivery Challan No."
                      value={receiveForm.dc_no}
                      onChange={(e) => setReceiveForm({ ...receiveForm, dc_no: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Remarks</Label>
                    <Input
                      placeholder="Any notes..."
                      value={receiveForm.remarks}
                      onChange={(e) => setReceiveForm({ ...receiveForm, remarks: e.target.value })}
                    />
                  </div>
                </div>

                {/* Total for this batch */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-sm text-purple-800">
                    <strong>Batch Total:</strong> {formatCurrency((parseFloat(receiveForm.labour_charges) || 0) + (parseFloat(receiveForm.other_charges) || 0))}
                  </p>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setReceiveDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleReceive} data-testid="receive-batch-btn">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Receive Batch
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dispatch Dialog */}
        <Dialog open={dispatchDialogOpen} onOpenChange={setDispatchDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-blue-600" />
                Create Dispatch Challan (DC)
              </DialogTitle>
              <DialogDescription>
                Select vendor and OJCs to create a consolidated dispatch challan
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Vendor Selection */}
              <div className="space-y-2">
                <Label>Select Vendor/Jobber *</Label>
                <Select value={selectedVendor} onValueChange={handleVendorSelect}>
                  <SelectTrigger data-testid="vendor-select">
                    <SelectValue placeholder="Select a vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* OJCs for selected vendor */}
              {selectedVendor && (
                <>
                  <div className="space-y-2">
                    <Label>Select OJCs to Dispatch ({vendorOjcs.length} pending)</Label>
                    {vendorOjcs.length === 0 ? (
                      <div className="text-center py-6 text-muted-foreground border rounded-lg">
                        No pending OJCs for this vendor
                      </div>
                    ) : (
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/50">
                              <TableHead className="w-12">Select</TableHead>
                              <TableHead>OJC No.</TableHead>
                              <TableHead>WO</TableHead>
                              <TableHead>Item</TableHead>
                              <TableHead>Process</TableHead>
                              <TableHead className="text-center">Pending Qty</TableHead>
                              <TableHead className="w-32">Dispatch Qty</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {vendorOjcs.map(ojc => {
                              const pendingQty = ojc.pending_dispatch_qty || (ojc.quantity_issued - (ojc.quantity_dispatched || 0));
                              const isSelected = selectedOjcsForDispatch[ojc.id] !== undefined;
                              
                              return (
                                <TableRow key={ojc.id} className={isSelected ? 'bg-blue-50' : ''}>
                                  <TableCell>
                                    <Checkbox
                                      checked={isSelected}
                                      onCheckedChange={() => toggleOjcSelection(ojc.id, pendingQty)}
                                      data-testid={`select-ojc-${ojc.id}`}
                                    />
                                  </TableCell>
                                  <TableCell className="font-mono font-bold text-purple-600">{ojc.ojc_number}</TableCell>
                                  <TableCell className="font-mono text-sm">{ojc.work_order_number}</TableCell>
                                  <TableCell className="text-sm">{ojc.item_name}</TableCell>
                                  <TableCell>{ojc.process_name}</TableCell>
                                  <TableCell className="text-center font-medium">{pendingQty}</TableCell>
                                  <TableCell>
                                    {isSelected && (
                                      <Input
                                        type="number"
                                        value={selectedOjcsForDispatch[ojc.id] || ''}
                                        onChange={(e) => updateDispatchQty(ojc.id, e.target.value)}
                                        max={pendingQty}
                                        className="w-24"
                                        data-testid={`dispatch-qty-${ojc.id}`}
                                      />
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>

                  {/* Dispatch Details */}
                  {Object.keys(selectedOjcsForDispatch).length > 0 && (
                    <div className="space-y-4 border-t pt-4">
                      <h4 className="font-semibold">Dispatch Details</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Dispatch Date *</Label>
                          <Input
                            type="date"
                            value={dispatchForm.dispatch_date}
                            onChange={(e) => setDispatchForm({ ...dispatchForm, dispatch_date: e.target.value })}
                            data-testid="dispatch-date-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Expected Return Date</Label>
                          <Input
                            type="date"
                            value={dispatchForm.expected_return_date}
                            onChange={(e) => setDispatchForm({ ...dispatchForm, expected_return_date: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Vehicle Number</Label>
                          <Input
                            value={dispatchForm.vehicle_number}
                            onChange={(e) => setDispatchForm({ ...dispatchForm, vehicle_number: e.target.value.toUpperCase() })}
                            placeholder="MH-12-AB-1234"
                            data-testid="vehicle-number-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Driver Name</Label>
                          <Input
                            value={dispatchForm.driver_name}
                            onChange={(e) => setDispatchForm({ ...dispatchForm, driver_name: e.target.value })}
                            placeholder="Driver name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Driver Phone</Label>
                          <Input
                            value={dispatchForm.driver_phone}
                            onChange={(e) => setDispatchForm({ ...dispatchForm, driver_phone: e.target.value })}
                            placeholder="9876543210"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Remarks</Label>
                          <Input
                            value={dispatchForm.remarks}
                            onChange={(e) => setDispatchForm({ ...dispatchForm, remarks: e.target.value })}
                            placeholder="Any notes..."
                          />
                        </div>
                      </div>

                      {/* Summary */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h5 className="font-semibold text-blue-800 mb-2">Dispatch Summary</h5>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <p>Selected OJCs: <strong>{Object.keys(selectedOjcsForDispatch).length}</strong></p>
                          <p>Total Qty: <strong>{Object.values(selectedOjcsForDispatch).reduce((a, b) => a + b, 0)}</strong></p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setDispatchDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleCreateDispatch} 
                disabled={Object.keys(selectedOjcsForDispatch).length === 0}
                data-testid="create-dispatch-btn"
              >
                <Truck className="w-4 h-4 mr-2" />
                Create Dispatch Challan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>OJC Details</DialogTitle>
              <DialogDescription>
                {detailOjc?.ojc_number}
              </DialogDescription>
            </DialogHeader>
            
            {detailOjc && (
              <div className="space-y-4 py-4">
                {/* Summary */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <p className="text-sm text-muted-foreground">Issued</p>
                    <p className="text-xl font-bold">{detailOjc.quantity_issued}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-sm text-green-600">Accepted</p>
                    <p className="text-xl font-bold text-green-700">{detailOjc.quantity_accepted}</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3 text-center">
                    <p className="text-sm text-red-600">Rejected</p>
                    <p className="text-xl font-bold text-red-700">{detailOjc.quantity_rejected}</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <p className="text-sm text-purple-600">Total Cost</p>
                    <p className="text-xl font-bold text-purple-700">{formatCurrency(detailOjc.total_ojc_cost || 0)}</p>
                  </div>
                </div>

                {/* Info */}
                <div className="bg-muted/50 rounded-lg p-3 text-sm">
                  <div className="grid grid-cols-3 gap-4">
                    <p><span className="text-muted-foreground">Process:</span> {detailOjc.process_name}</p>
                    <p><span className="text-muted-foreground">Jobber:</span> {detailOjc.jobber_name}</p>
                    <p><span className="text-muted-foreground">Work Order:</span> {detailOjc.work_order_number}</p>
                    <p><span className="text-muted-foreground">Rate/Unit:</span> ₹{(detailOjc.actual_rate_per_unit || 0).toFixed(2)}</p>
                    <p><span className="text-muted-foreground">Labour Charges:</span> {formatCurrency(detailOjc.total_labour_charges || 0)}</p>
                    <p><span className="text-muted-foreground">Status:</span> <Badge className={statusColors[detailOjc.status]}>{statusLabels[detailOjc.status]}</Badge></p>
                  </div>
                </div>

                {/* Dispatch History */}
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Truck className="w-4 h-4 text-blue-600" />
                    Dispatch History ({detailOjc.dispatch_history?.length || 0})
                  </h4>
                  {detailOjc.dispatch_history?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>DC No.</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-center">Quantity</TableHead>
                          <TableHead>Type</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {detailOjc.dispatch_history.map((dispatch, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-mono text-blue-600">{dispatch.dc_number}</TableCell>
                            <TableCell>{formatDate(dispatch.dispatch_date)}</TableCell>
                            <TableCell className="text-center font-medium">{dispatch.quantity}</TableCell>
                            <TableCell>
                              <Badge variant={dispatch.is_redispatch ? 'secondary' : 'default'}>
                                {dispatch.is_redispatch ? 'Re-dispatch' : 'Original'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-muted-foreground text-sm">No dispatch records yet</p>
                  )}
                </div>

                {/* Receipt History */}
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <PackageCheck className="w-4 h-4 text-green-600" />
                    Receipt History ({detailOjc.receipt_history?.length || 0})
                  </h4>
                  {detailOjc.receipt_history?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>GRN No.</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-center">OK Qty</TableHead>
                          <TableHead className="text-center">Rejected</TableHead>
                          <TableHead>Reason</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {detailOjc.receipt_history.map((receipt, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-mono text-green-600">{receipt.grn_number}</TableCell>
                            <TableCell>{formatDate(receipt.receipt_date)}</TableCell>
                            <TableCell className="text-center text-green-600 font-medium">{receipt.ok_qty}</TableCell>
                            <TableCell className="text-center text-red-600">{receipt.rejected_qty || 0}</TableCell>
                            <TableCell className="text-sm">{receipt.rejection_reason || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-muted-foreground text-sm">No receipt records yet</p>
                  )}
                </div>

                {/* Receiving Batches (Legacy) */}
                {detailOjc.receiving_batches?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <History className="w-4 h-4" />
                      Receiving Batches (Legacy) ({detailOjc.receiving_batches.length})
                    </h4>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Batch</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-center">Received</TableHead>
                          <TableHead className="text-center">Accepted</TableHead>
                          <TableHead className="text-center">Rejected</TableHead>
                          <TableHead className="text-right">Labour</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead>DC No.</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {detailOjc.receiving_batches.map((batch, idx) => (
                          <TableRow key={idx}>
                            <TableCell>Batch {batch.batch_no}</TableCell>
                            <TableCell>{formatDate(batch.received_date)}</TableCell>
                            <TableCell className="text-center">{batch.quantity_received}</TableCell>
                            <TableCell className="text-center text-green-600">{batch.quantity_accepted}</TableCell>
                            <TableCell className="text-center text-red-600">{batch.quantity_rejected || 0}</TableCell>
                            <TableCell className="text-right">{formatCurrency(batch.labour_charges || 0)}</TableCell>
                            <TableCell className="text-right font-medium">{formatCurrency(batch.total_batch_cost || 0)}</TableCell>
                            <TableCell>{batch.dc_no || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
